import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Button } from "@/components/ui/button";
import InsuranceBadge from "@/components/InsuranceBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { LrnChipSearch } from "@/components/ui/lrn-chip-search";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, DataTable, StatusBadge, EmptyState } from "@/components/ui-system";
import { toast } from "sonner";
import { Plus, Package, Calendar, Edit } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import AppointmentWarning from "@/components/AppointmentWarning";
import { TIME_RANGES, getMinAppointmentDate, getMaxAppointmentDate, isDelhiveryAppointmentBooked, validateReferenceRequiredForAppointment, validatePoExpiryAndAppointment, validateOrderPoExpiry } from "@/utils/appointmentUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";

const ORDER_STATUSES = [
  "CREATED", "MANIFESTED", "PICKED_UP", "LEFT_ORIGIN", "REACH_DESTINATION",
  "UNDEL_REATTEMPT", "PART_DEL", "OFD", "DELIVERED", "RETURNED_INTRANSIT",
  "RECEIVED_AT_RETURN_CENTER", "RETURN_OFD", "RETURN_DELIVERED", "NOT_PICKED", "LOST"
];

const TABS = [
  { id: 'new', label: 'New Orders', statuses: ['CREATED', 'MANIFESTED'] },
  { id: 'in_process', label: 'In-Process', statuses: ['ASSIGNED', 'PICKED_UP', 'LEFT_ORIGIN', 'REACH_DESTINATION', 'UNDEL_REATTEMPT', 'PART_DEL', 'OFD'] },
  { id: 'delivered', label: 'Delivered', statuses: ['DELIVERED'] },
  { id: 'returned', label: 'Returned', statuses: ['RETURNED', 'RETURNED_INTRANSIT', 'RECEIVED_AT_RETURN_CENTER', 'RETURN_OFD', 'RETURN_DELIVERED'] },
  { id: 'lost', label: 'LOST', statuses: ['LOST'] },
  { id: 'cancelled', label: 'Cancelled', statuses: ['CANCELLED'] },
  { id: 'not_picked', label: 'Not Picked', statuses: ['NOT_PICKED'] },
  { id: 'all', label: 'All', statuses: [] },
];

const ITEMS_PER_PAGE = 50;
const ZONE_CODES = ['N1', 'N2', 'E', 'NE', 'W1', 'W2', 'S1', 'S2', 'Central'];

// Statuses that allow appointment editing
const APPOINTMENT_EDITABLE_STATUSES = ['CREATED', 'APPOINTMENT_SET', 'MANIFESTED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_PICKUP', 'OFD'];

// The order's reference list (new array, else legacy single field). A "complete" reference has
// BOTH a number and a date — a legacy ref-without-date must be re-collected, not silently reused.
const refListOf = (o) => (o?.reference_numbers?.length
  ? o.reference_numbers
  : (o?.reference_number ? [{ reference_number: o.reference_number, date: o.reference_number_date || "" }] : []));
const hasCompleteRef = (o) => refListOf(o).some((r) => (r.reference_number || "").trim() && (r.date || "").trim());

// Final statuses where appointments should not be shown
const FINAL_STATUSES = ['DELIVERED', 'RETURN_DELIVERED', 'LOST', 'RETURN_OFD', 'CANCELLED'];

const SellerOrders = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);          // current page (server-paginated)
  const [tabCounts, setTabCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTimeRange, setAppointmentTimeRange] = useState("");
  // Inline reference/PO capture when setting an appointment on an order that has none yet.
  const [apptRefNumber, setApptRefNumber] = useState("");
  const [apptRefDate, setApptRefDate] = useState("");
  const [apptPoExpiry, setApptPoExpiry] = useState("");
  const [apptDelhiveryId, setApptDelhiveryId] = useState("");   // Delhivery appointment ID (required)
  const [activeTab, setActiveTab] = useLocalStorageState('seller-orders:tab', 'new');
  const [currentPage, setCurrentPage] = useState(1);

  // Search and Filter states (persisted across reloads/navigation)
  // Multi-value LRN/reference search: a set of chips; the backend gets them comma-joined.
  const [searchChips, setSearchChips] = useLocalStorageState('seller-orders:search-chips', []);
  const [selectedMarketplace, setSelectedMarketplace] = useLocalStorageState('seller-orders:marketplace', "");
  const [selectedStatus, setSelectedStatus] = useLocalStorageState('seller-orders:status', "");
  const [startDate, setStartDate] = useLocalStorageState('seller-orders:startDate', "");
  const [endDate, setEndDate] = useLocalStorageState('seller-orders:endDate', "");
  const [selectedOriginZone, setSelectedOriginZone] = useLocalStorageState('seller-orders:originZone', "");
  const [selectedDestZone, setSelectedDestZone] = useLocalStorageState('seller-orders:destZone', "");
  const [marketplaces, setMarketplaces] = useState([]);
  
  // Wallet balance state
  const [walletBalance, setWalletBalance] = useState(0);
  const [minWalletBalance, setMinWalletBalance] = useState(0);
  const [isLowBalance, setIsLowBalance] = useState(false);

  useEffect(() => {
    fetchMarketplaces();
    fetchWalletInfo();
  }, []);

  const searchKey = (searchChips || []).join(",");   // stable dep + the value sent to the server

  // Tab/filter change -> back to page 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchKey, selectedMarketplace, selectedStatus, startDate, endDate, selectedOriginZone, selectedDestZone]);

  // Server-side fetch: current tab + filters + page, plus tab counts.
  useEffect(() => {
    fetchOrders();
  }, [activeTab, currentPage, searchKey, selectedMarketplace, selectedStatus, startDate, endDate, selectedOriginZone, selectedDestZone]);

  const fetchWalletInfo = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`);
      const balance = response.data.balance || 0;
      const minBalance = response.data.min_wallet_balance || 0;
      const enforceWalletMinimum = response.data.enforce_wallet_minimum_for_orders || false;
      setWalletBalance(balance);
      setMinWalletBalance(minBalance);
      // Only flag as low balance if enforcement is enabled
      setIsLowBalance(enforceWalletMinimum && balance <= minBalance);
    } catch (error) {
      console.error("Failed to fetch wallet info");
    }
  };

  const buildOrderParams = (includeTabAndPage) => {
    const p = {};
    if (searchKey) p.search = searchKey;
    if (selectedMarketplace && selectedMarketplace !== "all") p.marketplace_id = selectedMarketplace;
    if (selectedStatus && selectedStatus !== "all") p.status = selectedStatus;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    if (selectedOriginZone && selectedOriginZone !== "all") p.origin_zone = selectedOriginZone;
    if (selectedDestZone && selectedDestZone !== "all") p.dest_zone = selectedDestZone;
    if (includeTabAndPage) { p.tab = activeTab; p.page = currentPage; p.limit = ITEMS_PER_PAGE; }
    return p;
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const [listRes, countsRes] = await Promise.all([
        axios.get(`${API}/seller/orders/list`, { params: buildOrderParams(true) }),
        axios.get(`${API}/seller/orders/tab-counts`, { params: buildOrderParams(false) }),
      ]);
      setOrders(listRes.data.orders || []);
      setTotal(listRes.data.total || 0);
      setTotalPages(listRes.data.total_pages || 1);
      setTabCounts(countsRes.data || {});
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketplaces = async () => {
    try {
      // /active is seller-accessible; /b2b-marketplace/ requires b2b_marketplace:view (admin only),
      // which 403s for sellers and silently leaves the list empty (filter + appointment-id flag).
      const response = await axios.get(`${API}/b2b-marketplace/active`);
      setMarketplaces(response.data);
    } catch (error) {
      console.error("Failed to fetch marketplaces");
    }
  };


  const handleClearFilters = () => {
    setSearchChips([]);
    setSelectedMarketplace("");
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    setSelectedOriginZone("");
    setSelectedDestZone("");
    setCurrentPage(1);
  };

  const openAppointmentDialog = (order, isEdit = false) => {
    setSelectedOrder(order);
    if (isEdit && order.appointment_date && order.appointment_time_range) {
      setAppointmentDate(order.appointment_date);
      setAppointmentTimeRange(order.appointment_time_range);
    } else if (isEdit && order.appointment_datetime) {
      const date = new Date(order.appointment_datetime);
      setAppointmentDate(date.toISOString().split('T')[0]);
      setAppointmentTimeRange("9:00 AM - 12:00 PM");
    } else {
      setAppointmentDate("");
      setAppointmentTimeRange("");
    }
    const firstRef = refListOf(order)[0] || {};
    setApptRefNumber(firstRef.reference_number || "");
    setApptRefDate(firstRef.date || "");
    setApptPoExpiry(order.po_expiry_date ? String(order.po_expiry_date).slice(0, 10) : "");
    setApptDelhiveryId(order.delhivery_appointment_id || order.appointment_id || "");
    setShowAppointmentDialog(true);
  };

  const handleAppointmentSubmit = async () => {
    if (!appointmentDate || !appointmentTimeRange) {
      toast.error("Please select appointment date and time range");
      return;
    }
    const apptIdRequired = !!marketplaces.find((m) => m.id === selectedOrder?.b2b_marketplace_id)?.appointment_id_required;
    if (apptIdRequired && !apptDelhiveryId.trim()) {
      toast.error("Appointment ID is required to book an appointment for this marketplace.");
      return;
    }

    // Setting an appointment requires a COMPLETE reference/PO number (number + date) and the order
    // PO expiry. If the order lacks a complete one, the dialog collects them inline and we send them.
    const orderHasCompleteRef = hasCompleteRef(selectedOrder);
    const orderHasPoExpiry = !!selectedOrder?.po_expiry_date;
    const collectedRef = !orderHasCompleteRef;
    const showPoExpiryField = !orderHasCompleteRef || !orderHasPoExpiry;
    const effRefs = collectedRef
      ? [{ reference_number: (apptRefNumber || "").trim(), date: (apptRefDate || "").trim() }].filter((r) => r.reference_number)
      : refListOf(selectedOrder);
    const effPoExpiry = showPoExpiryField ? (apptPoExpiry || "") : (selectedOrder?.po_expiry_date ? String(selectedOrder.po_expiry_date).slice(0, 10) : "");

    const refErr = validateReferenceRequiredForAppointment(effRefs, appointmentDate)
      || validatePoExpiryAndAppointment(effRefs, appointmentDate)
      || validateOrderPoExpiry(effRefs, effPoExpiry, appointmentDate);
    if (refErr) {
      toast.error(refErr);
      return;
    }

    try {
      const body = { appointment_date: appointmentDate, appointment_time_range: appointmentTimeRange, appointment_id: apptDelhiveryId.trim() };
      if (collectedRef) body.reference_numbers = effRefs;
      if (showPoExpiryField) body.po_expiry_date = effPoExpiry || null;
      const resp = await axios.put(
        `${API}/appointments/orders/${selectedOrder.id}/appointment`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );

      const booking = resp.data?.delhivery_booking;
      const baseMsg = selectedOrder.appointment_date || selectedOrder.appointment_datetime
        ? "Appointment updated successfully"
        : "Appointment added successfully";
      if (booking?.success) {
        toast.success(`${baseMsg} and booked with Delhivery!`);
      } else if (booking && !booking.skipped && booking.error) {
        toast.warning(`${baseMsg}, but Delhivery booking failed.`);
      } else {
        toast.success(baseMsg);
      }
      setShowAppointmentDialog(false);
      setAppointmentDate("");
      setAppointmentTimeRange("");
      setApptDelhiveryId("");
      fetchOrders();
    } catch (error) {
      console.error("Appointment update error:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          "Failed to update appointment";
      toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to update appointment");
    }
  };

  const getTabCount = (tabId) => tabCounts[tabId] ?? 0;
  const getPaginatedOrders = () => orders;   // server already returns the current page
  const showEdd = !['delivered', 'returned', 'lost', 'cancelled', 'not_picked'].includes(activeTab);

  const columns = [
    { key: 'lrn', header: 'LRN', cell: (order) => (
        <div className="flex flex-col items-start gap-1">
          {order.lrn ? (
            <button onClick={() => navigate(`/seller/orders/${order.id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">{order.lrn}</button>
          ) : <span className="text-sm text-slate-400">N/A</span>}
          <InsuranceBadge value={order.insurance_option} />
        </div>
      ) },
    { key: 'sp', header: 'Shipping Partner', cell: (order) => order.shipping_partner_name ? (
        <div className="text-sm"><span className="font-medium text-slate-900">{order.shipping_partner_name}</span>{order.actual_shipping_partner && <div className="text-xs text-slate-500">via {order.actual_shipping_partner}</div>}</div>
      ) : <span className="text-sm text-slate-400">-</span> },
    { key: 'zone', header: 'Zone', cell: (order) => (order.origin_zone || order.dest_zone) ? (
        <span className="text-sm text-slate-700 whitespace-nowrap">{order.origin_zone || '—'} <span className="text-slate-400">→</span> {order.dest_zone || '—'}</span>
      ) : <span className="text-sm text-slate-400">-</span> },
    { key: 'ref', header: 'Reference No.', primary: true, cell: (order) => (
        <button onClick={() => navigate(`/seller/orders/${order.id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">{order.InfoFromPartnerLRCopy?.shippers_reference_no || order.reference_number || '-'}</button>
      ) },
    { key: 'mp', header: 'Marketplace', cell: (order) => order.b2b_marketplace_name ? (
        <div className="flex items-center gap-2 justify-end md:justify-start">{order.b2b_marketplace_logo && <img src={order.b2b_marketplace_logo} alt={order.b2b_marketplace_name} className="h-6 w-6 object-contain" />}<span className="text-sm text-slate-600">{order.b2b_marketplace_name}</span></div>
      ) : <span className="text-sm text-slate-600">-</span> },
    { key: 'inv', header: 'Invoice No.', cell: (order) => <span className="text-sm text-slate-700">{order.InfoFromPartnerLRCopy?.invoice_no || order.invoice_number}</span> },
    { key: 'boxes', header: 'Boxes', align: 'right', cell: (order) => <span className="text-sm text-slate-700">{order.InfoFromPartnerLRCopy?.total_number_of_boxes || order.number_of_boxes}</span> },
    { key: 'amount', header: 'Amount', align: 'right', cell: (order) => <span className="text-sm font-medium text-slate-900">₹{(order.InfoFromPartnerLRCopy?.total_invoice_value ? parseFloat(order.InfoFromPartnerLRCopy.total_invoice_value) : (order.total_invoice_amount || order.invoice_amount || 0)).toLocaleString('en-IN')}</span> },
    { key: 'appt', header: 'Appointment', cell: (order) => (order.appointment_date || order.appointment_datetime) ? (
        <div className="flex items-center gap-3 justify-end md:justify-start">
          <span className="text-sm text-slate-600 whitespace-nowrap">{order.appointment_date ? (<span><span>{order.appointment_date}</span>{order.appointment_time_range && <span className="block text-xs text-slate-500">{order.appointment_time_range}</span>}</span>) : (<span><span className="block">{formatToIST(order.appointment_datetime, false)}</span><span className="block text-xs text-slate-500">{formatToIST(order.appointment_datetime).replace(formatToIST(order.appointment_datetime, false), '').replace(/^[,\s]+/, '')}</span></span>)}{isDelhiveryAppointmentBooked(order) && (<span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium" title={order.delhivery_appointment_id ? `Booked with Delhivery — Appointment ID: ${order.delhivery_appointment_id}` : "Booked with Delhivery"}>Booked ✓</span>)}</span>
          {APPOINTMENT_EDITABLE_STATUSES.includes(order.status) && (
            <button onClick={() => openAppointmentDialog(order, true)} disabled={isLowBalance} title={isLowBalance ? "Wallet is at/below the minimum — recharge required before editing the appointment" : "Edit appointment"} className="text-slate-900 hover:text-teal-600 transition-colors p-1.5 rounded hover:bg-teal-50 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-900 disabled:hover:bg-transparent"><Edit className="w-5 h-5" /></button>
          )}
        </div>
      ) : (!FINAL_STATUSES.includes(order.status) && (
        <Button size="sm" variant="outline" disabled={isLowBalance} title={isLowBalance ? "Wallet is at/below the minimum — recharge required before booking an appointment" : "Add appointment"} onClick={() => openAppointmentDialog(order, false)} className="text-xs"><Calendar className="w-3 h-3 mr-1" />Add</Button>
      )) },
    { key: 'created', header: 'Created', cell: (order) => {
        const createdDate = formatToIST(order.created_at, false);
        const createdTime = formatToIST(order.created_at).replace(createdDate, '').replace(/^[,\s]+/, '');
        return (
          <div className="whitespace-nowrap text-sm text-slate-600">
            <div>{createdDate}</div>
            {createdTime && <div className="text-xs text-slate-500">{createdTime}</div>}
          </div>
        );
      } },
    ...(showEdd ? [{ key: 'edd', header: 'EDD', cell: (order) => order.estimated_dates && order.estimated_dates.length > 0 ? (
        <span className="text-sm text-blue-600 font-medium">{(() => { const l = order.estimated_dates[order.estimated_dates.length - 1]; const d = l?.date || l; if (!d) return '-'; try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } })()}</span>
      ) : <span className="text-sm text-slate-400">-</span> }] : []),
    { key: 'status', header: 'Status', cell: (order) => <StatusBadge status={order.status} kind="order" /> },
    { key: 'actions', header: 'Actions', isActions: true, cell: (order) => (
        <Button size="sm" variant="outline" onClick={() => navigate(`/seller/orders/${order.id}`)}>View Details</Button>
      ) },
  ];

  const emptyNode = (
    <EmptyState
      icon={Package}
      title="No orders yet"
      description="Get started by creating your first order."
      action={
        <Button onClick={() => !isLowBalance && navigate('/seller/orders/create')} disabled={isLowBalance} className={isLowBalance ? 'opacity-70 cursor-not-allowed' : ''}>
          <Plus className="mr-2 h-4 w-4" />Create Order
        </Button>
      }
    />
  );

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Orders"
          subtitle="Manage and track your orders"
          actions={
            <div className="flex flex-col sm:items-end gap-1">
              <Button
                onClick={() => !isLowBalance && navigate('/seller/orders/create')}
                className={isLowBalance ? 'cursor-not-allowed opacity-70' : ''}
                disabled={isLowBalance}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Order
              </Button>
              {isLowBalance && (
                <p className="text-xs text-red-600 font-medium">
                  Low Balance: ₹{walletBalance.toLocaleString('en-IN')} (Min: ₹{minWalletBalance.toLocaleString('en-IN')})
                </p>
              )}
            </div>
          }
        />

        <AppointmentWarning />

        {/* Tabs — wrap onto multiple rows so all are visible without horizontal scrolling */}
        <div className="flex flex-wrap gap-x-1 gap-y-0 border-b border-slate-200 bg-white px-2 sm:px-4 rounded-t-lg shadow-lg">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-brand text-brand'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
              }`}
            >
              {tab.label} <span className="font-bold text-brand">({getTabCount(tab.id)})</span>
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <LrnChipSearch chips={searchChips || []} onChange={setSearchChips} maxChips={25} />
                </div>
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="whitespace-nowrap w-full sm:w-auto"
                >
                  Clear Filters
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">Marketplace</Label>
                  <SearchableSelect
                    value={selectedMarketplace}
                    onValueChange={setSelectedMarketplace}
                    placeholder="All Marketplaces"
                    options={[{ value: "all", label: "All Marketplaces" }, ...marketplaces.map((m) => ({ value: m.id, label: m.name }))]}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">Status</Label>
                  <SearchableSelect
                    value={selectedStatus}
                    onValueChange={setSelectedStatus}
                    placeholder="All Statuses"
                    options={[{ value: "all", label: "All Statuses" }, ...ORDER_STATUSES.map((s) => ({ value: s, label: s }))]}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">Origin Zone</Label>
                  <SearchableSelect
                    value={selectedOriginZone}
                    onValueChange={setSelectedOriginZone}
                    placeholder="All Origin Zones"
                    options={[{ value: "all", label: "All Origin Zones" }, ...ZONE_CODES.map((z) => ({ value: z, label: z }))]}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">Dest Zone</Label>
                  <SearchableSelect
                    value={selectedDestZone}
                    onValueChange={setSelectedDestZone}
                    placeholder="All Dest Zones"
                    options={[{ value: "all", label: "All Dest Zones" }, ...ZONE_CODES.map((z) => ({ value: z, label: z }))]}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <DataTable
          columns={columns}
          data={getPaginatedOrders()}
          rowKey={(o) => o.id}
          loading={loading}
          empty={emptyNode}
          pagination={totalPages > 1 ? {
            page: currentPage,
            pages: totalPages,
            total: total,
            pageSize: ITEMS_PER_PAGE,
            onPageChange: setCurrentPage,
          } : undefined}
        />
      </div>

      <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedOrder?.appointment_datetime ? 'Edit Appointment' : 'Add Appointment'}
            </DialogTitle>
            <DialogDescription>
              {selectedOrder?.appointment_date || selectedOrder?.appointment_datetime
                ? 'Update the appointment date and time range for this order' 
                : 'Set an appointment date and time range for this order'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <AppointmentWarning />
            <div>
              <Label>Appointment Date</Label>
              <Input
                type="date"
                value={appointmentDate}
                onChange={(e) => setAppointmentDate(e.target.value)}
                min={getMinAppointmentDate().toISOString().split('T')[0]}
                max={getMaxAppointmentDate().toISOString().split('T')[0]}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Time Range</Label>
              <Select
                value={appointmentTimeRange}
                onValueChange={setAppointmentTimeRange}
                disabled={!appointmentDate}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              {(() => { const req = !!marketplaces.find((m) => m.id === selectedOrder?.b2b_marketplace_id)?.appointment_id_required; return (<>
              <Label>Delhivery Appointment ID{req ? " *" : " (optional)"}</Label>
              <Input value={apptDelhiveryId} onChange={(e) => setApptDelhiveryId(e.target.value)} placeholder="Enter Delhivery appointment ID" className="mt-1" />
              {req && <p className="text-xs text-red-500 mt-1">Required to book the appointment for this marketplace.</p>}
              </>); })()}
            </div>
            {selectedOrder && !hasCompleteRef(selectedOrder) && (
              <>
                <p className="text-xs text-red-500">A reference/PO number (with its date and PO expiry) is required to set an appointment.</p>
                <div>
                  <Label>Reference / PO Number *</Label>
                  <Input value={apptRefNumber} onChange={(e) => setApptRefNumber(e.target.value)} placeholder="PO/Reference number" className="mt-1" />
                </div>
                <div>
                  <Label>Reference Date *</Label>
                  <Input type="date" value={apptRefDate} onChange={(e) => setApptRefDate(e.target.value)} className="mt-1" />
                </div>
              </>
            )}
            {selectedOrder && (!hasCompleteRef(selectedOrder) || !selectedOrder.po_expiry_date) && (
              <div>
                <Label>PO Expiry Date *</Label>
                <Input type="date" value={apptPoExpiry} onChange={(e) => setApptPoExpiry(e.target.value)} className="mt-1" />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowAppointmentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAppointmentSubmit}>
              {selectedOrder?.appointment_date || selectedOrder?.appointment_datetime ? 'Update Appointment' : 'Add Appointment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default SellerOrders;
